import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../../components/theme/theme';
import CommonStatusChip from '../../../components/ui-components/StatusChipComponent';
import { useTranslation } from '../../../utils/translations';

interface SupportRequestMobileCardProps {
    row: Record<string, unknown>;
}

const supportStatusColors: Record<string, string> = {
    Open: colors.bluePreset[30],
    'In Progress': colors.warningPreset[70],
    Closed: colors.successPreset[30],
};

/**
 * Mobile/tablet-only card rendering of a single support-requests row for the support screen
 * (pages/support/index.tsx). Desktop keeps using CommonTable.
 */
export const SupportRequestMobileCard: React.FC<SupportRequestMobileCardProps> = ({ row }) => {
    const { t } = useTranslation();
    const ticketNumber = String(row.ticketNumber || 'N/A');
    const subject = String(row.subject || 'N/A');
    const requestType = String(row.requestType || 'N/A');
    const date = String(row.date || 'N/A');
    const status = String(row.status || 'N/A');
    const statusColor = supportStatusColors[status] ?? colors.greyPreset[80];

    return (
        <Box
            sx={{
                border: `1px solid ${colors.greyPreset[30]}`,
                borderRadius: 1.5,
                backgroundColor: colors.fullWhite,
                overflow: 'hidden',
                p: 2,
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        color={colors.greyPreset[100]}
                        fontWeight={700}
                        noWrap
                        sx={{ textTransform: 'uppercase' }}
                    >
                        {t('support.table.ticketNumber')}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                        #{ticketNumber}
                    </Typography>
                </Stack>
                <CommonStatusChip label={status} chipBgColor={statusColor} sx={{ flexShrink: 0 }} />
            </Stack>

            <Box
                sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: colors.greyPreset[90],
                }}
            >
                <Stack gap={0.25} sx={{ minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        color={colors.greyPreset[100]}
                        fontWeight={700}
                        sx={{ textTransform: 'uppercase' }}
                    >
                        {t('support.table.subject')}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                        {subject}
                    </Typography>
                </Stack>
            </Box>

            <Box
                sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: colors.greyPreset[90],
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)',
                    columnGap: 3,
                    alignItems: 'start',
                }}
            >
                <Stack gap={0.25} sx={{ minWidth: 0, textAlign: 'left' }}>
                    <Typography
                        variant="caption"
                        color={colors.greyPreset[100]}
                        fontWeight={700}
                        sx={{ textTransform: 'uppercase' }}
                    >
                        {t('support.table.requestType')}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                        {requestType}
                    </Typography>
                </Stack>
                <Stack gap={0.25} alignItems="flex-end" sx={{ minWidth: 0, textAlign: 'right' }}>
                    <Typography
                        variant="caption"
                        color={colors.greyPreset[100]}
                        fontWeight={700}
                        sx={{ textTransform: 'uppercase' }}
                    >
                        {t('support.table.date')}
                    </Typography>
                    <Stack direction="row" gap={0.5} alignItems="center">
                        <span className="icon-calendar" style={{ fontSize: 14, color: colors.greyPreset[100] }} />
                        <Typography variant="body2" fontWeight={600} noWrap>
                            {date}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
};
